/**
 * Module dependencies.
 */

import {
  NoteEntityType,
  useCreateLinkedNote,
  useLinkNote,
  useNoteLinks,
  useUnlinkNote
} from 'app/hooks/requests/apps-notes/use-note-links';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from 'app/components/ui/dialog';
import { FilePlus, Link2, NotebookText, Paperclip, X } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { Button } from 'app/components/ui/button';
import { Input } from 'app/components/ui/input';
import { Spinner } from 'app/components/ui/spinner';
import { appRoutes } from 'app/routes';
import { useNavigate } from 'react-router-dom';
import { useNotesSearch } from 'app/hooks/requests/apps-notes/use-notes-search';
import { useToast } from 'app/components/providers/toast-provider/toast-provider';
import { useTranslation } from 'react-i18next';

/**
 * `Props` type.
 */

type Props = {
  entityType: NoteEntityType;
  entityId: string | undefined;
};

/**
 * Export `NoteLinksPanel` — lists/creates/attaches notes linked to an entity.
 */

export function NoteLinksPanel({ entityType, entityId }: Props): JSX.Element | null {
  const [translate] = useTranslation();
  const navigate = useNavigate();
  const { error: toastError, success: toastSuccess } = useToast();
  const { data: links, isLoading } = useNoteLinks(entityType, entityId);
  const createLinked = useCreateLinkedNote();
  const unlink = useUnlinkNote();
  const linkedNoteIds = useMemo(() => new Set((links ?? []).map(link => link.noteId)), [links]);

  const [creating, setCreating] = useState(false);
  const [attaching, setAttaching] = useState(false);
  const [name, setName] = useState('');

  if (!entityId) {
    return null;
  }

  const openNote = (noteId: string) => navigate(`${appRoutes.dashboard.notes.base}?note=${noteId}`);

  const handleCreate = async () => {
    const trimmed = name.trim();

    if (!trimmed) {
      return;
    }

    try {
      const noteId = await createLinked.mutateAsync({ name: trimmed, entityType, entityId });

      setCreating(false);
      setName('');

      if (noteId) {
        openNote(noteId);
      }
    } catch (err) {
      toastError({ message: translate('notes.links.createFailed') });
    }
  };

  const handleUnlink = async (linkId: string) => {
    try {
      await unlink.mutateAsync({ linkId, entityType, entityId });
      toastSuccess({ message: translate('notes.links.unlinked') });
    } catch (err) {
      toastError({ message: translate('notes.links.unlinkFailed') });
    }
  };

  return (
    <div className={'rounded-xl border border-border bg-card shadow-sm'}>
      <div className={'flex items-center justify-between gap-3 border-b border-border px-5 py-3.5'}>
        <div className={'flex items-center gap-2 text-sm font-semibold text-foreground'}>
          <Link2 className={'h-4 w-4'} />

          {translate('notes.links.title')}
        </div>

        <div className={'flex items-center gap-2'}>
          <Button onClick={() => setAttaching(true)} size={'sm'} type={'button'} variant={'outline'}>
            <Paperclip className={'mr-1.5 h-3.5 w-3.5'} />

            {translate('notes.links.attach')}
          </Button>

          <Button onClick={() => setCreating(true)} size={'sm'} type={'button'}>
            <FilePlus className={'mr-1.5 h-3.5 w-3.5'} />

            {translate('notes.links.newNote')}
          </Button>
        </div>
      </div>

      <div className={'p-3'}>
        {isLoading ? (
          <div className={'grid place-items-center py-8'}>
            <Spinner />
          </div>
        ) : !links || links.length === 0 ? (
          <div className={'flex flex-col items-center gap-1 py-8 text-center'}>
            <NotebookText className={'h-8 w-8 opacity-40'} />

            <p className={'text-sm text-muted-foreground'}>{translate('notes.links.empty')}</p>
          </div>
        ) : (
          <ul className={'flex flex-col gap-1'}>
            {links.map(link => (
              <li className={'group flex items-center gap-2 rounded-md px-2 py-2 hover:bg-muted'} key={link.linkId}>
                <button
                  className={'flex min-w-0 flex-1 items-start gap-2 text-left'}
                  onClick={() => openNote(link.noteId)}
                  type={'button'}
                >
                  <NotebookText className={'mt-0.5 h-4 w-4 shrink-0 text-muted-foreground'} />

                  <span className={'min-w-0'}>
                    <span className={'block truncate text-sm font-medium text-foreground'}>{link.name}</span>

                    {!!link.snippet && (
                      <span className={'block truncate text-xs text-muted-foreground'}>{link.snippet}</span>
                    )}
                  </span>
                </button>

                <button
                  className={
                    'shrink-0 rounded p-1 text-muted-foreground opacity-0 hover:bg-background hover:text-destructive group-hover:opacity-100'
                  }
                  onClick={() => handleUnlink(link.linkId)}
                  title={translate('notes.links.unlink')}
                  type={'button'}
                >
                  <X className={'h-4 w-4'} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* New note dialog */}
      <Dialog onOpenChange={setCreating} open={creating}>
        <DialogContent className={'sm:max-w-[420px]'}>
          <DialogHeader>
            <DialogTitle>{translate('notes.links.newNote')}</DialogTitle>
          </DialogHeader>

          <Input
            autoFocus
            onChange={event => setName(event.target.value)}
            onKeyDown={event => event.key === 'Enter' && handleCreate()}
            placeholder={translate('common.labels.name')}
            value={name}
          />

          <DialogFooter>
            <Button onClick={() => setCreating(false)} type={'button'} variant={'outline'}>
              {translate('common.actions.cancel')}
            </Button>

            <Button disabled={!name.trim() || createLinked.isLoading} onClick={handleCreate} type={'button'}>
              {translate('common.actions.create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Attach existing dialog */}
      <AttachDialog
        entityId={entityId}
        entityType={entityType}
        linkedNoteIds={linkedNoteIds}
        onOpenChange={setAttaching}
        open={attaching}
      />
    </div>
  );
}

/**
 * `AttachDialog` — search the user's notes and link one.
 */

function AttachDialog({
  entityId,
  entityType,
  linkedNoteIds,
  onOpenChange,
  open
}: {
  entityId: string;
  entityType: NoteEntityType;
  linkedNoteIds: Set<string>;
  onOpenChange: (open: boolean) => void;
  open: boolean;
}): JSX.Element {
  const [translate] = useTranslation();
  const { error: toastError, success: toastSuccess } = useToast();
  const [query, setQuery] = useState('');
  const { data: results, isFetching } = useNotesSearch(query);
  const link = useLinkNote();

  const handleAttach = async (noteId: string) => {
    try {
      await link.mutateAsync({ noteId, entityType, entityId });
      toastSuccess({ message: translate('notes.links.attached') });
      onOpenChange(false);
      setQuery('');
    } catch (err) {
      toastError({ message: translate('notes.links.attachFailed') });
    }
  };

  return (
    <Dialog
      onOpenChange={value => {
        onOpenChange(value);

        if (!value) {
          setQuery('');
        }
      }}
      open={open}
    >
      <DialogContent className={'sm:max-w-[480px]'}>
        <DialogHeader>
          <DialogTitle>{translate('notes.links.attachTitle')}</DialogTitle>
        </DialogHeader>

        <Input
          autoFocus
          onChange={event => setQuery(event.target.value)}
          placeholder={translate('notes.search.placeholder')}
          value={query}
        />

        <div className={'max-h-[320px] overflow-y-auto'}>
          {query.trim().length < 2 ? (
            <p className={'py-6 text-center text-sm text-muted-foreground'}>{translate('notes.links.searchHint')}</p>
          ) : isFetching ? (
            <div className={'grid place-items-center py-6'}>
              <Spinner />
            </div>
          ) : !results || results.length === 0 ? (
            <p className={'py-6 text-center text-sm text-muted-foreground'}>{translate('notes.search.empty')}</p>
          ) : (
            <ul className={'flex flex-col gap-1'}>
              {results.map(result => {
                const already = linkedNoteIds.has(result.id);

                return (
                  <li key={result.id}>
                    <button
                      className={
                        'flex w-full items-center gap-2 rounded-md px-2 py-2 text-left hover:bg-muted disabled:opacity-50'
                      }
                      disabled={already || link.isLoading}
                      onClick={() => handleAttach(result.id)}
                      type={'button'}
                    >
                      <NotebookText className={'h-4 w-4 shrink-0 text-muted-foreground'} />

                      <span className={'min-w-0 flex-1'}>
                        <span className={'block truncate text-sm font-medium text-foreground'}>{result.name}</span>

                        {result.path.length > 0 && (
                          <span className={'block truncate text-xs text-muted-foreground'}>
                            {result.path.join(' / ')}
                          </span>
                        )}
                      </span>

                      {already && (
                        <span className={'shrink-0 text-xs text-muted-foreground'}>
                          {translate('notes.links.alreadyLinked')}
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
